/**
 * Currency and number formatting utilities
 */

import { getLocale } from './device'

/**
 * Format currency value with proper locale support
 */
export function formatCurrency(
  value: string | number | null | undefined,
  currency: string = 'USD',
  minimumFractionDigits: number = 2
): string | null | undefined {
  if (value === null || value === undefined || value === '') {
    return value as string | null | undefined
  }

  let numValue = typeof value === 'number' ? value : parseFloat(value)

  if (isNaN(numValue)) {
    return value as string
  }

  try {
    const formatter = new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: minimumFractionDigits,
    })

    return formatter.format(numValue)
  } catch {
    // Fallback for invalid currency or locale
    return `${currency === 'USD' ? '$' : currency} ${numValue.toFixed(minimumFractionDigits)}`
  }
}

/**
 * Format number with locale support
 */
export function formatNumber(value: string | number, options?: Intl.NumberFormatOptions): string {
  const numValue = typeof value === 'number' ? value : parseFloat(value)

  if (isNaN(numValue)) {
    return value.toString()
  }

  try {
    return new Intl.NumberFormat(getLocale(), options).format(numValue)
  } catch {
    return numValue.toLocaleString()
  }
}

/**
 * Calculate price after discount
 */
export function calculatePriceWithDiscount(originalPrice: number, discountPercentage: number): number {
  // Validate discount percentage
  if (discountPercentage < 0 || discountPercentage > 100) {
    return originalPrice
  }

  const discountAmount = (originalPrice * discountPercentage) / 100
  const finalPrice = originalPrice - discountAmount

  // Round to 2 decimal places
  return Math.round(finalPrice * 100) / 100
}

/**
 * Format percentage value
 */
export function formatPercentage(value: number, locale?: string): string {
  try {
    const formatter = new Intl.NumberFormat(locale || getLocale(), {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

    return formatter.format(value)
  } catch {
    return `${Math.round(value * 100)}%`
  }
}

/**
 * Calculate price with discount percentage
 * @param price Original price
 * @param discountPercent Discount percentage (0-100)
 * @returns Discounted price
 */
export function calculatePrice(price: number, discountPercent: number = 0): number {
  // Validate discount percentage
  if (discountPercent < 0 || discountPercent > 100) {
    return price
  }

  const discountAmount = price * (discountPercent / 100)
  const finalPrice = price - discountAmount

  // Round to 2 decimal places
  return Math.round(finalPrice * 100) / 100
}

/**
 * Calculate extended (line) price for a cart item given the current session. Honors session_price,
 * customer membership pricing, selected variant price, then falls back to base campaign_product price.
 */
export function calculateItemPrice(item: any, session?: any): number {
  if (!item) {
    return 0
  }
  const qty = Number(item.quantity) || 0
  const cp = item.campaign_product || {}

  if (cp.session_price != null) {
    return Number(cp.session_price) * qty
  }
  if (session?.customer?.has_membership && cp.membership_price != null) {
    return Number(cp.membership_price) * qty
  }
  if (item.variant?.price != null) {
    return Number(item.variant.price) * qty
  }
  return (Number(cp.price) || 0) * qty
}
